/*
 |------------------------------------------------------------------------------
 | Enhanced ecommerce tracking
 |------------------------------------------------------------------------------
 |
 | This piece of logic aims to pass every enhanced ecommerce object
 | to Google Tag Manager, so we can track user interaction and promotions
 | more clearly.
 |
 */

import { isEqual } from 'lodash';
import containerManager from '../../01-helpers/filter-context/container-manager';
import { pushProductImpressions, pushProductClick } from './impressions';
import { pushPromoView, pushPromoClick } from './promotions';

/* ========================================================================== */

const selectors = {
	container: '.js-enhanced-ecommerce-container',
	item: '.js-enhanced-ecommerce-item'
};

/* ========================================================================== */

function bindClickEvent(currentContainer) {
	const containerElement = document.querySelector(selectors.container);

	if (!containerElement) {
		return;
	}

	// Since our elements are appended after an endpoint call and change
	// regularly, we bind the click event on the parent and check to see
	// if the clicked element is in fact an item element.
	containerElement.addEventListener('click', (event) => {
		const clickedItem = event.target.closest(selectors.item);

		if (!clickedItem || !currentContainer.state?.response?.ecommerce) {
			return;
		}

		// These are both pushed, the functions themselves check if we actually
		// should push the provided data, they never both go off, always one.
		pushProductClick(clickedItem, currentContainer.state.response.ecommerce);
		pushPromoClick(clickedItem, currentContainer.state.response.ecommerce);
	});
}

function bindViewEvents(currentContainer) {
	currentContainer.onUpdate(({ currentState }) => {
		// When we have previously pushed a view object,
		// and its still the same one as in the current state.
		//
		// We don't do anything, its already pushed.
		if (
			!currentState.response?.ecommerce ||
			isEqual(
				bindViewEvents.previouslyPushedObject,
				currentState.response?.ecommerce
			)
		) {
			return;
		}

		bindViewEvents.previouslyPushedObject = currentState.response.ecommerce;

		pushProductImpressions(currentState.response?.ecommerce);
		pushPromoView(currentState.response?.ecommerce);
	});
}

function init() {
	const currentContainer = containerManager.getContainer();

	bindViewEvents(currentContainer);
	bindClickEvent(currentContainer);
}

init();

/* ========================================================================== */

export const baseEvent = {
	eventLabel: '',
	eventAction: 'list',
	eventCategory: 'ecommerce'
};
